/**
 * W-14 取消/退款(WScreen-14)。买家从 W-13 IN_PROGRESS+NONE「申请取消订单」进入。
 * 退款说明卡 + 表单(原因/说明 10–500/证据占位)+ 二次确认(C-5)→ requestCancellation
 * → toast + 回 W-13。视觉真源 design/cancellation-request.html。
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  requestCancellation,
  REASON_OPTIONS,
  type CancellationReasonCode,
} from '@/api/aftersales';
import { getOrderDetail } from '@/api/order';
import { uploadReportEvidence } from '@/api/report';
import { errorMessage, formatAmount } from '@/lib/orderState';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const MIN_DESC = 10;
const MAX_DESC = 500;
const MAX_EVIDENCE = 3;

export function CancellationRequestPage() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [reasonCode, setReasonCode] = useState<CancellationReasonCode | ''>('');
  const [description, setDescription] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 证据 = {url(入库/提交的私有桶 base URL), preview(本地 blob,仅本页预览)}。
  const [evidence, setEvidence] = useState<{ url: string; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const submittingRef = useRef(false);

  const onPickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || evidence.length >= MAX_EVIDENCE) return;
    setUploading(true);
    // 预览用本地 blob:证据落私有桶,入库 URL 匿名 GET 会 403(裂图),预览直接显示刚选的文件本身。
    const preview = URL.createObjectURL(file);
    try {
      const url = await uploadReportEvidence(file);
      setEvidence((prev) => [...prev, { url, preview }]);
    } catch (err) {
      URL.revokeObjectURL(preview);
      toast.error(errorMessage(err, '图片上传失败,请重试'));
    } finally {
      setUploading(false);
    }
  };
  const removeEvidence = (idx: number) =>
    setEvidence((prev) => {
      const target = prev[idx];
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== idx);
    });

  // 退款说明卡:订单金额来自详情(仅只读展示,不改记账)。
  const { data: detail } = useQuery({
    queryKey: ['order-detail', orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: orderId !== '',
    retry: 0,
  });

  const mutation = useMutation({
    mutationFn: () =>
      requestCancellation(orderId, {
        reasonCode: reasonCode as CancellationReasonCode,
        description: description.trim(),
        evidenceUrls: evidence.map((e) => e.url),
      }),
    onSuccess: () => {
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['order-detail', orderId] });
      queryClient.invalidateQueries({ queryKey: ['my-orders'] });
      toast.success('取消申请已提交,请等待卖家响应');
      navigate(`/orders/${orderId}`);
    },
    onError: (err) => {
      submittingRef.current = false;
      setConfirmOpen(false);
      setErrorMsg(errorMessage(err, '提交失败,请稍后重试'));
    },
  });

  const trimmedLen = description.trim().length;
  const submitDisabled =
    reasonCode === '' || trimmedLen < MIN_DESC || mutation.isPending || uploading;

  const openConfirm = () => {
    setErrorMsg('');
    if (submitDisabled) return;
    setConfirmOpen(true);
  };

  const doSubmit = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    mutation.mutate(undefined, {
      onSettled: () => {
        // 成功走 navigate 离屏;失败在 onError 已复位。
      },
    });
  };

  const amount = detail?.amount;
  // 售后(COMPLETED 后置退款)与取消(IN_PROGRESS)复用本页,后端按订单态自动分派;仅文案区分。
  const isAftersale =
    (location.state as { aftersale?: boolean } | null)?.aftersale === true ||
    detail?.state === 'COMPLETED';
  const title = isAftersale ? '申请售后' : '申请取消';

  return (
    <div className="inner section">
      <div className="container-read">
        <div className="sm muted mb-6">{title}</div>
        <h1 className="h2 mb-2">{isAftersale ? '申请售后' : '申请取消订单'}</h1>
        <p className="muted sm mb-8">
          提交后将通知卖家处理;卖家同意或 7 天内未响应,订单退款并按原路返回。
        </p>

        {/* 退款说明(只读;口径引用 settlement-rules §五) */}
        <div className="card card-pad mb-8">
          <h3 className="h3 mb-4">退款说明</h3>
          <div className="row between mb-2">
            <span className="muted sm">订单金额</span>
            <span className="sm">{amount != null ? formatAmount(amount) : '—'}</span>
          </div>
          <div className="row between mb-2">
            <span className="muted sm">预计退款金额</span>
            <span className="price" style={{ fontSize: 'var(--text-xl-size)' }}>
              {amount != null ? formatAmount(amount) : '—'}
            </span>
          </div>
          <div className="row between">
            <span className="muted sm">退款方式</span>
            <span className="sm">原路退回(付款账户)</span>
          </div>
          <p className="hint mt-4">
            退款将在卖家同意取消后按原路返回你的付款账户,预计 1–3 个工作日到账。
          </p>
        </div>

        {/* 取消申请表单 */}
        <div className="card card-pad">
          <div className="field">
            <label className="label" htmlFor="cancel-reason">
              {isAftersale ? '售后原因' : '取消原因'}
            </label>
            <select
              className="select"
              id="cancel-reason"
              value={reasonCode}
              onChange={(e) => {
                setReasonCode(e.target.value as CancellationReasonCode);
                setErrorMsg('');
              }}
            >
              <option value="" disabled>
                请选择取消原因
              </option>
              {REASON_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="cancel-desc">
              补充说明
            </label>
            <textarea
              className="textarea"
              id="cancel-desc"
              maxLength={MAX_DESC}
              placeholder="请说明取消原因(至少 10 字,最多 500 字),便于卖家与平台核实。"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value.slice(0, MAX_DESC));
                setErrorMsg('');
              }}
            />
            <div className="row between mt-2">
              <span className="hint">建议填写至少 10 字</span>
              <span
                className="hint"
                style={{ color: trimmedLen < MIN_DESC ? 'var(--color-destructive)' : undefined }}
              >
                {trimmedLen}/{MAX_DESC}
                {trimmedLen < MIN_DESC ? `(至少 ${MIN_DESC} 字)` : ''}
              </span>
            </div>
          </div>

          <div className="field">
            <span className="label">上传凭证(选填,最多 {MAX_EVIDENCE} 张)</span>
            <div className="row gap-4 wrap">
              {evidence.map((item, idx) => (
                <div className="review-thumb" key={item.url}>
                  <img src={item.preview} alt={`凭证 ${idx + 1}`} />
                  <button
                    type="button"
                    className="review-thumb-del"
                    aria-label="删除图片"
                    onClick={() => removeEvidence(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {evidence.length < MAX_EVIDENCE && (
                <label className="upload-box" style={{ width: 96, height: 96 }}>
                  <span>{uploading ? '上传中…' : '+ 上传'}</span>
                  <input type="file" accept="image/*" hidden disabled={uploading} onChange={onPickFile} />
                </label>
              )}
            </div>
            <p className="hint">可上传聊天记录、付款截图等凭证,帮助平台更快核实。支持 JPG / PNG。</p>
          </div>

          {errorMsg && (
            <p className="sm mt-2" role="alert" style={{ color: 'var(--color-destructive)' }}>
              {errorMsg}
            </p>
          )}

          <button
            type="button"
            className="btn btn-destructive btn-block btn-lg mt-4"
            disabled={submitDisabled}
            onClick={openConfirm}
          >
            {isAftersale ? '提交售后申请' : '提交取消申请'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-block mt-3"
            onClick={() => navigate(`/orders/${orderId}`)}
          >
            返回订单详情
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={isAftersale ? '确认提交售后申请?' : '确认提交取消申请?'}
        description={`提交后将通知卖家处理。卖家同意或 7 天内未响应,${
          amount != null ? formatAmount(amount) : '订单金额'
        }将按原路退回你的付款账户。`}
        confirmLabel="确认提交"
        confirmVariant="destructive"
        submitting={mutation.isPending}
        onConfirm={doSubmit}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
